import { useEffect, useRef, useState } from 'react';

/**
 * Throttle hook - limits updates to a maximum frequency.
 * Optimized to avoid timer thrashing (constant create/delete of timers) 
 * on high-frequency inputs like mouse movement.
 *
 * @param value - Value to throttle
 * @param delay - Minimum time between updates in milliseconds (default: 16ms = ~60fps)
 * @returns Throttled value
 */
export function useThrottle<T>(value: T, delay: number = 16): T {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  const latestValue = useRef(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRan = useRef(Date.now());

  // Keep ref updated with the latest value so the timer can access it
  useEffect(() => {
    latestValue.current = value;
  }, [value]);

  useEffect(() => {
    // If a timer is already running, do nothing. 
    // This prevents "thrashing" (clearing and resetting) the timer on every value change.
    if (timeoutRef.current) {
      return;
    }

    const now = Date.now();
    const timeSinceLast = now - lastRan.current;
    const nextDelay = Math.max(0, delay - timeSinceLast);

    const loop = () => {
      // Update state to the latest value tracked by the ref
      setThrottledValue(latestValue.current);
      lastRan.current = Date.now();
      
      // Clear the timeout ref so the next value change can schedule a new timer
      timeoutRef.current = null;
    };

    timeoutRef.current = setTimeout(loop, nextDelay);

  }, [value, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return throttledValue;
}